import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useInView } from 'react-intersection-observer'
import { shallowEqual, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { useHistory, useLocation } from 'react-router-dom'
import block from 'bem-cn'

import { useExitAnimation } from 'shared/hooks/useExitAnimation'
import { localeCommonKeysType } from 'shared/locale/en-US'
import { ReactComponent as ArrowSVG } from 'shared/img/arrow.svg'
import { mediaSize } from 'shared/style/var'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { addNotify } from 'components/Notify'

import { IHomeMenuItem, IHomeMenuSubItem } from '../data'

import './MenuItem.scss'

interface IMenuItemProps {
  name: localeCommonKeysType
  subItems?: IHomeMenuSubItem[]
  isOpen: boolean
  index: number
  item: IHomeMenuItem
  isSideMenu?: boolean
  onOpen: (ind: number) => void
  onClose: () => void
  callback?: () => void
}

const b = block('menu-item')

export const MenuItem: React.FC<IMenuItemProps> = ({
  callback,
  isSideMenu,
  name,
  subItems,
  isOpen,
  onOpen,
  onClose,
  index,
  item,
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null)
  const history = useHistory()
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const { slots: localeSlots, common: localeCommon } = useSelector(selectLocaleDict, shallowEqual)
  const { pathname, search } = useLocation()
  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.tablet })
  const isFakeSidemenuOpen = useExitAnimation({ disappearDuration: 350, realIsOpen: isOpen })

  const { ref, inView } = useInView({ threshold: 0.1 })

  useEffect(() => {
    if (!inView && isOpen) {
      onClose()
    }
  }, [inView, isOpen, onClose])

  const [state, setState] = useState<{ top: number | string; left: number; bottom: number } | null>(null)

  const getMenuPosition = useCallback(() => {
    if (!menuRef.current) return { top: 0, left: 0, bottom: 0 }
    const rect = menuRef.current.getBoundingClientRect()

    if (node) {
      node.style.position = isMobileOrTablet ? 'fixed' : 'static'
    }

    return isMobileOrTablet
      ? { top: 'unset', bottom: isSideMenu ? 0 : 74, left: 0, width: window.innerWidth }
      : { top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, bottom: 0, width: 'auto' }
  }, [isMobileOrTablet, isSideMenu])

  useEffect(() => {
    if (!isFakeSidemenuOpen) return
    setState(getMenuPosition())
    const animationFrameId = requestAnimationFrame(() => setState(getMenuPosition()))
  }, [isFakeSidemenuOpen, getMenuPosition])

  const handleItemClick = useCallback(
    (link: IHomeMenuItem) => {
      if (!isAuth && link.authRequired) {
        addNotify(localeSlots.pleaseAuth)
        return
      }
      if (callback) {
        callback()
      }
      history.push(link.route)
    },
    [history, isAuth, localeSlots.pleaseAuth, callback]
  )

  const node = document.getElementById('nav-item')
  const isActive = useMemo(() => item.route === `${pathname}${search}`, [item.route, pathname, search])

  const content =
    isFakeSidemenuOpen && subItems?.length
      ? ReactDOM.createPortal(
          <div className={b('sub-menu-container', { closed: !isOpen })} style={state || {}}>
            <div className={b('sub-menu')}>
              {subItems.map((subItem, indx) => (
                <div
                  key={indx}
                  className={b('sub-menu-item', { isActive: subItem.route === `${pathname}${search}` })}
                  onClick={() => handleItemClick(subItem)}>
                  {localeCommon[subItem.textID]}
                </div>
              ))}
            </div>
          </div>,
          node!
        )
      : null

  const handleMenuClick = useCallback(() => {
    if (subItems) {
      if (isOpen) {
        onClose()
      } else {
        onOpen(index)
      }
    } else {
      handleItemClick(item)
    }
  }, [subItems, isOpen, handleItemClick, item, onClose, onOpen, index])

  return (
    <div className={b({ isSideMenu })} ref={menuRef}>
      <div className={b('menu-header', { active: isOpen || isActive })} onClick={handleMenuClick} ref={ref}>
        {subItems?.length ? <ArrowSVG className={b('arrow', { active: isOpen || isActive })} /> : null}
        <span className={b('title')}>{localeCommon[name]}</span>
        {item.image && (
          <div className={b('icon-svg')}>
            <img src={item.image} alt={item.textID} className={b('icon')} />
          </div>
        )}
      </div>
      {node ? content : null}
    </div>
  )
}
